import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ProjetsService } from 'app/services/projets/projets.service';
import { AwscontrolService, UploadResponse, DeleteResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';

type MainUploadMode = 'direct' | 'imageproduit';
type GalleryUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-modifier-projet',
  templateUrl: './modifier-projet.component.html',
  styleUrls: ['./modifier-projet.component.scss']
})
export class ModifierProjetComponent implements OnInit {
  @ViewChild('agencySelect') agencySelect!: MatSelect;
  @ViewChild('agencySearchInput') agencySearchInput!: ElementRef;

  // ============================================================
  // ✅ RÉFÉRENCES AUX ÉDITEURS contenteditable (WYSIWYG)
  // ============================================================
  @ViewChild('descFrEditor') descFrEditor?: ElementRef<HTMLElement>;
  @ViewChild('descArEditor') descArEditor?: ElementRef<HTMLElement>;
  @ViewChild('descEnEditor') descEnEditor?: ElementRef<HTMLElement>;
  // ============================================================

  projectForm!: FormGroup;
  isLoading = false;
  isSaving = false;
  idProjetAgency: string;

  // ==================== MAIN IMAGE ====================
  mainImage: string = '';
  mainUploadMode: MainUploadMode = 'direct';
  mainPreviewOriginalUrl: string | null = null;
  mainPreviewProcessedUrl: string | null = null;
  isAwaitingMainValidation: boolean = false;
  uploadingMain: boolean = false;

  // ==================== GALLERY ====================
  galleryImages: string[] = [];
  galleryUploadMode: GalleryUploadMode = 'direct';
  galleryPreviewOriginalUrls: string[] = [];
  galleryPreviewProcessedUrls: string[] = [];
  isAwaitingGalleryValidation: boolean = false;
  uploadingGallery: boolean = false;

  allAgencies: any[] = [];
  filteredAgencies: any[] = [];
  
  categories: string[] = [];
  categoriesArab: string[] = [];
  categoriesEnglish: string[] = [];
  categoryInput: string = '';
  categoryInputArab: string = '';
  categoryInputEnglish: string = '';

  selectedAgencyId: string | null = null;
  agencyAvailableLanguages: string[] = ['FR'];

  statusOptions = [
    { value: 'DISPONIBLE', label: 'Disponible', color: '#4CAF50' },
    { value: 'EN COURS', label: 'En cours', color: '#2196F3' },
    { value: 'VENDU', label: 'Vendu', color: '#F44336' }
  ];

  agencySearchTerm: string = '';
  isAgencyDropdownOpen: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private router: Router,
    private projetService: ProjetsService,
    private agenciesService: AgenciesService,
    private awsService: AwscontrolService,
    private fb: FormBuilder
  ) {
    this.idProjetAgency = this.activatedRoute.snapshot.paramMap.get('id') || '';
    this.initForm();
  }

  initForm(): void {
    this.projectForm = this.fb.group({
      agencyName: ['', Validators.required],
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      nameArab: ['', [Validators.minLength(3), Validators.maxLength(100)]],
      nameEnglish: ['', [Validators.minLength(3), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      descriptionArab: ['', [Validators.minLength(10)]],
      descriptionEnglish: ['', [Validators.minLength(10)]],
      adresse: ['', Validators.maxLength(200)],
      status: ['DISPONIBLE', Validators.required],
      videoUrlProjet: ['', this.urlValidator],
      nomCommercial: ['', Validators.maxLength(50)],
      nomCommercialArab: ['', Validators.maxLength(50)],
      nomCommercialEnglish: ['', Validators.maxLength(50)],
      numCommercial: ['', Validators.pattern('^[0-9+\\s-]*$')],
      emailCommercial: ['', Validators.email],
      categoryList: [[]],
      categoryListArab: [[]],
      categoryListEnglish: [[]]
    });
  }

  urlValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (!control.value || control.value === '') return null;
    try {
      new URL(control.value);
      return null;
    } catch {
      return { invalidUrl: true };
    }
  }

  ngOnInit(): void {
    this.loadAgencies();
    this.loadProject();
  }

  loadProject(): void {
    this.isLoading = true;
    this.projetService.getProjet(this.idProjetAgency).subscribe({
      next: (data: any) => {
        this.populateForm(data);
        this.updateAgencyLanguagesFromProject(data);
        this.isLoading = false;
        
        // Sync editors after data load
        setTimeout(() => this.syncEditors(), 100);
        setTimeout(() => this.syncEditors(), 300);
      },
      error: (error: any) => {
        console.error('Error loading project:', error);
        this.isLoading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger les données du projet'
        }).then(() => {
          this.location.back();
        });
      }
    });
  }

  populateForm(project: any): void {
    // Load images: first one = main, rest = gallery
    if (project.photos && Array.isArray(project.photos)) {
      const cleanPhotos = project.photos.filter((url: any) => url && typeof url === 'string' && url.trim() !== '');
      this.mainImage = cleanPhotos[0] || '';
      this.galleryImages = cleanPhotos.slice(1);
    } else {
      this.mainImage = '';
      this.galleryImages = [];
    }

    // Load categories
    if (project.categoryList && Array.isArray(project.categoryList)) {
      this.categories = [...project.categoryList];
    }
    if (project.categoryListArab && Array.isArray(project.categoryListArab)) {
      this.categoriesArab = [...project.categoryListArab];
    }
    if (project.categoryListEnglish && Array.isArray(project.categoryListEnglish)) {
      this.categoriesEnglish = [...project.categoryListEnglish];
    }

    // Set agency ID for navigation
    if (project.user?.id) {
      this.selectedAgencyId = project.user.id.toString();
    } else if (project.userId) {
      this.selectedAgencyId = project.userId.toString();
    }

    // Patch form values
    this.projectForm.patchValue({
      name: project.name || '',
      nameArab: project.nameArab || '',
      nameEnglish: project.nameEnglish || '',
      description: project.description || '',
      descriptionArab: project.descriptionArab || '',
      descriptionEnglish: project.descriptionEnglish || '',
      adresse: project.adresse || '',
      agencyName: project.agencyName || '',
      status: project.status || 'DISPONIBLE',
      videoUrlProjet: project.videoUrlProjet || '',
      nomCommercial: project.nomCommercial || '',
      nomCommercialArab: project.nomCommercialArab || '',
      nomCommercialEnglish: project.nomCommercialEnglish || '',
      numCommercial: project.numCommercial || '',
      emailCommercial: project.emailCommercial || '',
    });

    this.projectForm.patchValue({ categoryList: this.categories });
    this.projectForm.patchValue({ categoryListArab: this.categoriesArab });
    this.projectForm.patchValue({ categoryListEnglish: this.categoriesEnglish });
  }

  private updateAgencyLanguagesFromProject(project: any): void {
    const agencyId = project.user?.id || project.userId;
    if (agencyId) {
      const found = this.allAgencies.find(a => a.id === agencyId);
      if (found) {
        this.agencyAvailableLanguages = found.availableLanguages || ['FR'];
        if (this.agencyAvailableLanguages.length === 0) {
          this.agencyAvailableLanguages = ['FR'];
        }
      }
    }
  }

  loadAgencies(): void {
    this.isLoading = true;
    this.agenciesService.getList().subscribe({
      next: (data: any) => {
        let agencies: any[] = [];
        if (Array.isArray(data)) agencies = data;
        else if (data) agencies = [data];

        this.allAgencies = agencies
          .filter((agency: any) => agency && agency.name && typeof agency.name === 'string' && agency.role === 'promoteur-immobilieres')
          .map((agency: any) => ({
            id: agency.id,
            name: agency.name.trim(),
            role: agency.role,
            availableLanguages: agency.availableLanguages || ['FR'],
            initial: agency.name.trim().charAt(0).toUpperCase(),
            color: this.getRandomColor()
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));

        this.filteredAgencies = [...this.allAgencies];
        this.isLoading = false;

        if (this.allAgencies.length === 0) {
          Swal.fire({
            icon: 'info',
            title: 'Aucun promoteur disponible',
            text: 'Aucune agence avec le rôle "promoteur immobilier" n\'a été trouvée'
          });
        }

        if (this.selectedAgencyId) {
          const found = this.allAgencies.find(a => a.id === parseInt(this.selectedAgencyId!));
          if (found) {
            this.agencyAvailableLanguages = found.availableLanguages || ['FR'];
          }
        }
      },
      error: (error: any) => {
        console.error('Erreur lors du chargement des agences:', error);
        this.allAgencies = [];
        this.filteredAgencies = [];
        this.isLoading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger la liste des promoteurs'
        });
      }
    });
  }

  private getRandomColor(): string {
    const colors = ['#FF6B6B', '#4ECDC4', '#FFD166', '#06D6A0', '#118AB2', '#EF476F', '#7B68EE', '#20B2AA', '#FFA07A', '#9370DB'];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  onAgencySelectOpened(): void {
    this.isAgencyDropdownOpen = true;
    this.agencySearchTerm = '';
    this.filterAgencies();
    setTimeout(() => {
      if (this.agencySearchInput) this.agencySearchInput.nativeElement.focus();
    }, 100);
  }

  onAgencySelectClosed(): void {
    this.isAgencyDropdownOpen = false;
    this.agencySearchTerm = '';
    this.filterAgencies();
  }

  filterAgencies(): void {
    if (!this.agencySearchTerm || this.agencySearchTerm.trim() === '') {
      this.filteredAgencies = [...this.allAgencies];
    } else {
      const term = this.agencySearchTerm.toLowerCase().trim();
      this.filteredAgencies = this.allAgencies.filter((agency: any) => agency.name.toLowerCase().includes(term));
    }
  }

  clearAgencySearch(): void {
    this.agencySearchTerm = '';
    this.filterAgencies();
    if (this.agencySearchInput) this.agencySearchInput.nativeElement.focus();
  }

  selectAgency(agency: any): void {
    this.projectForm.patchValue({ agencyName: agency.name });
    this.selectedAgencyId = agency.id;
    this.agencyAvailableLanguages = agency.availableLanguages || ['FR'];
    if (this.agencyAvailableLanguages.length === 0) {
      this.agencyAvailableLanguages = ['FR'];
    }
    this.agencySelect.close();
    // Re-sync editors if language fields visibility changes
    setTimeout(() => this.syncEditors(), 100);
  }

  /* ============================================================
     ✅ SYNC DES ÉDITEURS contenteditable
     ============================================================ */
  private syncEditors(): void {
    if (this.descFrEditor) {
      const html = this.projectForm.get('description')?.value || '';
      if (this.descFrEditor.nativeElement.innerHTML !== html) {
        this.descFrEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descArEditor) {
      const html = this.projectForm.get('descriptionArab')?.value || '';
      if (this.descArEditor.nativeElement.innerHTML !== html) {
        this.descArEditor.nativeElement.innerHTML = html;
      }
    }
    if (this.descEnEditor) {
      const html = this.projectForm.get('descriptionEnglish')?.value || '';
      if (this.descEnEditor.nativeElement.innerHTML !== html) {
        this.descEnEditor.nativeElement.innerHTML = html;
      }
    }
  }

  /* ============================================================
     ✅ HANDLERS (input) DES CONTENTEDITABLE
     ============================================================ */
  onDescFrInput(): void {
    if (this.descFrEditor) {
      const html = this.descFrEditor.nativeElement.innerHTML;
      this.projectForm.get('description')?.setValue(html);
      this.projectForm.get('description')?.markAsDirty();
    }
  }

  onDescArInput(): void {
    if (this.descArEditor) {
      const html = this.descArEditor.nativeElement.innerHTML;
      this.projectForm.get('descriptionArab')?.setValue(html);
      this.projectForm.get('descriptionArab')?.markAsDirty();
    }
  }

  onDescEnInput(): void {
    if (this.descEnEditor) {
      const html = this.descEnEditor.nativeElement.innerHTML;
      this.projectForm.get('descriptionEnglish')?.setValue(html);
      this.projectForm.get('descriptionEnglish')?.markAsDirty();
    }
  }

  /* ============================================================
     BOLD — seule action de rich text exposée
     ============================================================ */
  makeBold(): void {
    try {
      document.execCommand('bold', false);
    } catch (e) {
      console.warn('execCommand("bold") failed:', e);
    }
  }

  // ============================================================
  // ==================== HELPERS ===============================
  // ============================================================
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'JPEG, PNG, JPG, GIF, WEBP uniquement', timer: 2000 });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'Max 5MB', timer: 2000 });
      return false;
    }
    return true;
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ============================================================
  // ==================== MAIN IMAGE ============================
  // ============================================================

  setMainUploadMode(mode: MainUploadMode): void {
    if (this.uploadingMain) return;
    if (this.isAwaitingMainValidation) this.cancelMainPreview(true);
    this.mainUploadMode = mode;
  }

  onMainImageSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    if (!this.validateImageFile(file)) { input.value = ''; return; }

    this.fileToDataUrl(file).then(d => { this.mainPreviewOriginalUrl = d; });

    if (this.mainUploadMode === 'direct') {
      this.uploadMainDirect(file, input);
    } else {
      this.uploadMainViaImageproduit(file, input);
    }
  }

  private uploadMainDirect(file: File, input: HTMLInputElement): void {
    this.uploadingMain = true;
    this.awsService.uploadFile(file, 'project-images').subscribe({
      next: (response: UploadResponse) => {
        this.uploadingMain = false;
        if (response.success && response.urls && response.urls.length) {
          this.mainImage = response.urls[0];
          this.mainPreviewOriginalUrl = null;
          Swal.fire({ icon: 'success', title: 'Image principale mise à jour', timer: 1500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Upload failed' });
          this.mainPreviewOriginalUrl = null;
        }
        input.value = '';
      },
      error: (err: any) => {
        this.uploadingMain = false;
        console.error(err);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger l\'image' });
        this.mainPreviewOriginalUrl = null;
        input.value = '';
      }
    });
  }

  private uploadMainViaImageproduit(file: File, input: HTMLInputElement): void {
    this.uploadingMain = true;
    this.isAwaitingMainValidation = false;
    this.mainPreviewProcessedUrl = null;

    this.awsService.imageproduit([file], 'project-images').subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainPreviewProcessedUrl = response.urls[0];
          this.isAwaitingMainValidation = true;
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: (response.errors && response.errors[0]) || 'Échec du traitement', confirmButtonColor: '#f44336' });
          this.mainPreviewOriginalUrl = null;
        }
        input.value = '';
      },
      error: () => {
        this.uploadingMain = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter l\'image principale', confirmButtonColor: '#f44336' });
        this.mainPreviewOriginalUrl = null;
        input.value = '';
      }
    });
  }

  validateMainPreview(): void {
    if (!this.mainPreviewProcessedUrl) return;
    this.mainImage = this.mainPreviewProcessedUrl;
    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;
    Swal.fire({ icon: 'success', title: 'Image principale validée', timer: 1500, showConfirmButton: false });
  }

  cancelMainPreview(silent: boolean = false): void {
    const url = this.mainPreviewProcessedUrl;
    const doCancel = () => {
      if (url) this.awsService.deleteFile(url).subscribe({ error: () => {} });
      this.mainPreviewOriginalUrl = null;
      this.mainPreviewProcessedUrl = null;
      this.isAwaitingMainValidation = false;
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser cette image ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  removeMainImage(): void {
    if (this.isAwaitingMainValidation) { this.cancelMainPreview(); return; }
    if (!this.mainImage) return;
    Swal.fire({
      title: 'Supprimer l\'image principale ?',
      text: 'Cette action est irréversible',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(this.mainImage).subscribe({
          next: (response: DeleteResponse) => {
            if (response.status === 'success') {
              this.mainImage = '';
              Swal.fire({ icon: 'success', title: 'Image supprimée', timer: 1500, showConfirmButton: false });
            } else {
              Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Échec de la suppression' });
            }
          },
          error: (err: any) => {
            console.error(err);
            Swal.fire({ icon: 'error', title: 'Erreur', text: 'Échec de la suppression' });
          }
        });
      }
    });
  }

  // ============================================================
  // ==================== GALERIE ===============================
  // ============================================================

  setGalleryUploadMode(mode: GalleryUploadMode): void {
    if (this.uploadingGallery) return;
    if (this.isAwaitingGalleryValidation) this.cancelGalleryPreview(true);
    this.galleryUploadMode = mode;
  }

  onGalleryImagesSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      if (this.validateImageFile(files[i])) validFiles.push(files[i]);
    }
    if (validFiles.length === 0) { input.value = ''; return; }

    if (this.galleryUploadMode === 'direct') {
      this.uploadGalleryDirect(validFiles, input);
    } else {
      this.galleryPreviewOriginalUrls = [];
      validFiles.forEach((f: File) => {
        this.fileToDataUrl(f).then(d => { this.galleryPreviewOriginalUrls.push(d); });
      });
      this.uploadGalleryViaImagesousproduit(validFiles, input);
    }
  }

  private uploadGalleryDirect(files: File[], input: HTMLInputElement): void {
    this.uploadingGallery = true;
    const filesArray: File[] = Array.from(files);
    const renamedFiles = filesArray.map((file: File) => {
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      const newName = `project-images/${timestamp}_${randomString}_${file.name}`;
      return new File([file], newName, { type: file.type });
    });

    this.awsService.uploadFiles(renamedFiles, 'project-images').subscribe({
      next: (response: UploadResponse) => {
        this.uploadingGallery = false;
        if (response.success && response.urls && response.urls.length) {
          this.galleryImages.push(...response.urls);
          Swal.fire({ icon: 'success', title: `${response.urls.length} image(s) ajoutée(s)`, timer: 1500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Upload failed' });
        }
        input.value = '';
      },
      error: (err: any) => {
        this.uploadingGallery = false;
        console.error(err);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger les images' });
        input.value = '';
      }
    });
  }

  private uploadGalleryViaImagesousproduit(files: File[], input: HTMLInputElement): void {
    this.uploadingGallery = true;
    this.isAwaitingGalleryValidation = false;
    this.galleryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, 'project-images').subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingGallery = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryPreviewProcessedUrls = response.urls;
          this.isAwaitingGalleryValidation = true;
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: (response.errors && response.errors[0]) || 'Échec du traitement', confirmButtonColor: '#f44336' });
          this.galleryPreviewOriginalUrls = [];
        }
        input.value = '';
      },
      error: () => {
        this.uploadingGallery = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter les images', confirmButtonColor: '#f44336' });
        this.galleryPreviewOriginalUrls = [];
        input.value = '';
      }
    });
  }

  validateGalleryPreview(): void {
    if (this.galleryPreviewProcessedUrls.length === 0) return;
    this.galleryImages.push(...this.galleryPreviewProcessedUrls);
    this.galleryPreviewOriginalUrls = [];
    this.galleryPreviewProcessedUrls = [];
    this.isAwaitingGalleryValidation = false;
    Swal.fire({ icon: 'success', title: 'Images validées', timer: 1500, showConfirmButton: false });
  }

  cancelGalleryPreview(silent: boolean = false): void {
    const urls = [...this.galleryPreviewProcessedUrls];
    const doCancel = () => {
      urls.forEach((u: string) => this.awsService.deleteFile(u).subscribe({ error: () => {} }));
      this.galleryPreviewOriginalUrls = [];
      this.galleryPreviewProcessedUrls = [];
      this.isAwaitingGalleryValidation = false;
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser ces images ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  moveGalleryImageUp(index: number): void {
    if (index === 0) return;
    const temp = this.galleryImages[index];
    this.galleryImages[index] = this.galleryImages[index - 1];
    this.galleryImages[index - 1] = temp;
  }

  moveGalleryImageDown(index: number): void {
    if (index === this.galleryImages.length - 1) return;
    const temp = this.galleryImages[index];
    this.galleryImages[index] = this.galleryImages[index + 1];
    this.galleryImages[index + 1] = temp;
  }

  setPrimaryFromGallery(index: number): void {
    const newMain = this.galleryImages[index];
    const oldMain = this.mainImage;
    this.galleryImages.splice(index, 1);
    if (oldMain) this.galleryImages.push(oldMain);
    this.mainImage = newMain;
    Swal.fire({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
      icon: 'success',
      title: 'Image principale définie'
    });
  }

  removeGalleryImage(index: number): void {
    const imageUrl = this.galleryImages[index];
    Swal.fire({
      title: 'Supprimer cette image ?',
      text: 'Cette action est irréversible',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.awsService.deleteFile(imageUrl).subscribe({
          next: (response: DeleteResponse) => {
            if (response.status === 'success') {
              this.galleryImages.splice(index, 1);
              Swal.fire({ icon: 'success', title: 'Image supprimée', timer: 1500, showConfirmButton: false });
            } else {
              Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Échec de la suppression' });
            }
          },
          error: (err: any) => {
            console.error(err);
            Swal.fire({ icon: 'error', title: 'Erreur', text: 'Échec de la suppression' });
          }
        });
      }
    });
  }

  // ============================================================
  // ==================== CATÉGORIES ============================
  // ============================================================

  addCategory(): void {
    if (this.categoryInput && this.categoryInput.trim() !== '') {
      const category = this.categoryInput.trim();
      if (!this.categories.includes(category)) {
        this.categories.push(category);
        this.projectForm.patchValue({ categoryList: this.categories });
      }
      this.categoryInput = '';
    }
  }

  removeCategory(category: string): void {
    const index = this.categories.indexOf(category);
    if (index >= 0) {
      this.categories.splice(index, 1);
      this.projectForm.patchValue({ categoryList: this.categories });
    }
  }

  addCategoryArab(): void {
    if (this.categoryInputArab && this.categoryInputArab.trim() !== '') {
      const category = this.categoryInputArab.trim();
      if (!this.categoriesArab.includes(category)) {
        this.categoriesArab.push(category);
        this.projectForm.patchValue({ categoryListArab: this.categoriesArab });
      }
      this.categoryInputArab = '';
    }
  }

  removeCategoryArab(category: string): void {
    const index = this.categoriesArab.indexOf(category);
    if (index >= 0) {
      this.categoriesArab.splice(index, 1);
      this.projectForm.patchValue({ categoryListArab: this.categoriesArab });
    }
  }

  addCategoryEnglish(): void {
    if (this.categoryInputEnglish && this.categoryInputEnglish.trim() !== '') {
      const category = this.categoryInputEnglish.trim();
      if (!this.categoriesEnglish.includes(category)) {
        this.categoriesEnglish.push(category);
        this.projectForm.patchValue({ categoryListEnglish: this.categoriesEnglish });
      }
      this.categoryInputEnglish = '';
    }
  }

  removeCategoryEnglish(category: string): void {
    const index = this.categoriesEnglish.indexOf(category);
    if (index >= 0) {
      this.categoriesEnglish.splice(index, 1);
      this.projectForm.patchValue({ categoryListEnglish: this.categoriesEnglish });
    }
  }

  // ============================================================
  // ==================== SUBMIT ================================
  // ============================================================

  onSubmit(): void {
    if (this.isAwaitingMainValidation) {
      Swal.fire({ icon: 'warning', title: 'Validation requise', text: 'Veuillez valider ou refuser l\'image principale.', confirmButtonColor: '#ff9800' });
      return;
    }
    if (this.isAwaitingGalleryValidation) {
      Swal.fire({ icon: 'warning', title: 'Validation requise', text: 'Veuillez valider ou refuser les images secondaires.', confirmButtonColor: '#ff9800' });
      return;
    }

    // 🔥 Récupérer les valeurs depuis les éditeurs contenteditable AVANT validation
    this.onDescFrInput();
    this.onDescArInput();
    this.onDescEnInput();

    if (this.projectForm.invalid) {
      this.markFormGroupTouched(this.projectForm);
      const invalidControl = document.querySelector('.ng-invalid');
      if (invalidControl) invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      Swal.fire({ icon: 'error', title: 'Formulaire invalide', text: 'Veuillez corriger les erreurs dans le formulaire' });
      return;
    }

    const allPhotos: string[] = [];
    if (this.mainImage) allPhotos.push(this.mainImage);
    allPhotos.push(...this.galleryImages);

    if (allPhotos.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Images requises', text: 'Veuillez ajouter au moins une image pour le projet' });
      return;
    }

    const formValue = this.projectForm.value;
    const updateData: any = {
      ...formValue,
      photos: allPhotos,
      categoryList: this.categories,
      categoryListArab: this.categoriesArab,
      categoryListEnglish: this.categoriesEnglish
    };

    Object.keys(updateData).forEach((key: string) => {
      if (updateData[key] === '' || updateData[key] === null || updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    this.isSaving = true;
    this.projetService.updateProjet(this.idProjetAgency, updateData).subscribe({
      next: () => {
        this.isSaving = false;
        Swal.fire({
          icon: 'success',
          title: 'Projet mis à jour !',
          text: 'Les modifications ont été enregistrées avec succès',
          showConfirmButton: false,
          timer: 2000
        }).then(() => {
          if (this.selectedAgencyId) {
            this.router.navigate(['/list-projet', this.selectedAgencyId]);
          } else {
            this.location.back();
          }
        });
      },
      error: (error: any) => {
        this.isSaving = false;
        console.error('Error updating project:', error);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Une erreur est survenue lors de la mise à jour du projet' });
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) this.markFormGroupTouched(control);
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.projectForm.get(fieldName);
    if (!field || !field.errors || !field.touched) return '';
    if (field.errors['required']) return 'Ce champ est requis';
    if (field.errors['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} caractères`;
    if (field.errors['maxlength']) return `Maximum ${field.errors['maxlength'].requiredLength} caractères`;
    if (field.errors['email']) return 'Email invalide';
    if (field.errors['pattern']) return 'Format invalide';
    if (field.errors['invalidUrl']) return 'URL invalide';
    return '';
  }

  navigateBack(): void {
    this.location.back();
  }

  getStatusColor(status: string): string {
    const statusOption = this.statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : '#757575';
  }
}